import { SdkContext } from '../internal'
import { versionedSettingsWrite } from '@/lib/realtime/settingsVersion'

/** F-119: the fixed categories of the Memberfy directory (hub.memberfy.net). */
export type HubCategory =
  | 'career' | 'business' | 'leadership' | 'technology' | 'marketing' | 'health'
  | 'education' | 'finance' | 'creativity' | 'community' | 'coworking'

/** How the community shows in the directory, as owner and admin see it. */
export interface HubListingSettings {
  listed: boolean
  category: HubCategory | null
  coverUrl: string | null
  showMemberCount: boolean
  /** Set when Memberfy took the community out of the directory: it stays hidden even with `listed` on. */
  delisted: { at: string; reason: string | null } | null
  /** The accepted categories, labeled in the request's language. */
  categories: Array<{ code: HubCategory; label: string }>
}

export type HubListingUpdate = Partial<Pick<HubListingSettings, 'listed' | 'category' | 'coverUrl' | 'showMemberCount'>>

/** The SuperAdmin's view of one community in the directory. */
export interface AdminHubListing {
  listed: boolean
  category: HubCategory | null
  delisted: boolean
  delistedAt: string | null
  /** The SuperAdmin's `Member` id. */
  delistedById: string | null
  reason: string | null
}

/**
 * F-119: the community directory. `get`/`update` are the community's own
 * settings (owner and admin of the `X-CommunityId`); `adminGet`/`adminSet` are
 * the SuperAdmin's curation, with the target community in the path.
 */
export class HubListingResource {
  constructor(private ctx: SdkContext) {}

  async get(): Promise<HubListingSettings> {
    return this.ctx.call({ httpMethod: 'GET', path: '/hub-listing' })
  }

  /** Changes only the fields sent. */
  async update(body: HubListingUpdate): Promise<HubListingSettings> {
    return versionedSettingsWrite((v) => this.ctx.call<HubListingSettings>({ httpMethod: 'PUT', path: '/hub-listing', hasBody: true }, { body: { ...body, ...(v ? { settingsUpdatedAt: v } : {}) } }))
  }

  async adminGet(communityId: string): Promise<AdminHubListing> {
    return this.ctx.call({ httpMethod: 'GET', path: '/admin/communities/{communityId}/hub-listing' }, { communityId })
  }

  /** `delisted: true` takes it out (reason of 10 to 2,000 characters required); `false` puts it back (reason optional). */
  async adminSet(communityId: string, delisted: boolean, reason?: string): Promise<AdminHubListing & { changed: boolean }> {
    return this.ctx.call(
      { httpMethod: 'PUT', path: '/admin/communities/{communityId}/hub-listing', hasBody: true },
      { communityId, body: reason ? { delisted, reason } : { delisted } }
    )
  }
}
